import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "dropdown", "inputs", "hiddenFields"]
  static values = {
    type:     String,
    area:     Number,
    editable: { type: Boolean, default: true },
    debounceMs: { type: Number, default: 300 }
  }

  connect() {
    this.timer = null
    this.abortController = null
    this._onInput = (event) => {
      if (event.target.matches('[data-location-picker-target="input"]')) {
        this.scheduleSearch(event.target)
      }
    }
    this.element.addEventListener('input', this._onInput)
    this._onChange = (event) => {
      if (!this.editableValue) return
      if (event.target.matches('[data-location-picker-target="input"]')) {
        this.writeMirror(event.target)
        this.markDirty()
      }
    }
    this.element.addEventListener('change', this._onChange)
    this._onAutosaved = (event) => this.reconcileIds(event.detail)
    document.addEventListener("territory:autosaved", this._onAutosaved)
  }

  disconnect() {
    this.abortController?.abort()
    if (this.timer) clearTimeout(this.timer)
    this.element.removeEventListener('input', this._onInput)
    this.element.removeEventListener('change', this._onChange)
    document.removeEventListener("territory:autosaved", this._onAutosaved)
  }

  /* ---- index allocation (DOM-derived, stateless) ---- */

  nextIndex() {
    const re = /territory\[territory_locations_attributes\]\[(\d+)\]/
    const used = [...document.querySelectorAll('input[name^="territory[territory_locations_attributes]"]')]
      .map(el => Number(el.name.match(re)?.[1]))
      .filter(n => Number.isInteger(n))
    return used.length ? Math.max(...used) + 1 : 0
  }

  /* ---- add / delete row ---- */

  addRow(event) {
    if (!this.editableValue) return
    const i = this.nextIndex()
    const wrapper = document.createElement('div')
    wrapper.className = 'form-field-wrapper'
    wrapper.dataset.index = String(i)
    wrapper.style.cssText = 'margin-bottom:12px;display:flex;width:100%'

    const inputWrapper = document.createElement('div')
    inputWrapper.className = 'form-field__input-wrapper'
    inputWrapper.style.width = '100%'

    const inputEl = document.createElement('input')
    inputEl.type = 'text'
    inputEl.className = 'form-field__input'
    inputEl.setAttribute('data-location-picker-target', 'input')
    inputEl.dataset.index = String(i)
    inputEl.autocomplete = 'off'
    inputEl.placeholder = `Enter ${this.typeValue}...`
    inputEl.name = `territory[territory_locations_attributes][${i}][${this.typeValue}]`

    const dropdown = document.createElement('div')
    dropdown.className = 'state-dropdown'
    dropdown.setAttribute('data-location-picker-target', 'dropdown')
    dropdown.style.cssText = 'display:none;position:relative;background-color:white;border:1px solid #ccc;width:100%;max-height:200px;overflow-y:auto'

    inputWrapper.appendChild(inputEl)
    inputWrapper.appendChild(dropdown)

    const deleteBtn = document.createElement('button')
    deleteBtn.type = 'button'
    deleteBtn.className = 'delete-input-button'
    deleteBtn.setAttribute('data-action', 'location-picker#deleteRow')
    deleteBtn.style.cssText = 'background:transparent;border:none;cursor:pointer;color:black'

    const icon = document.createElement('i')
    icon.className = 'bi bi-x'
    deleteBtn.appendChild(icon)

    wrapper.appendChild(inputWrapper)
    wrapper.appendChild(deleteBtn)

    this.inputsTarget.appendChild(wrapper)
  }

  deleteRow(event) {
    if (!this.editableValue) return
    const wrapper = event.target.closest('.form-field-wrapper')
    if (!wrapper) return
    const i = wrapper.dataset.index
    const destroyField = document.getElementById('location_' + i + '_destroy')
    if (destroyField) destroyField.value = 'true'
    wrapper.remove()
    this.markDirty()
  }

  /* ---- suggestions ---- */

  scheduleSearch(inputElement) {
    if (this.timer) clearTimeout(this.timer)
    this.timer = setTimeout(() => this.performSearch(inputElement), this.debounceMsValue)
  }

  async performSearch(inputElement) {
    if (this.abortController) this.abortController.abort()
    this.abortController = new AbortController()
    const raw = inputElement.value.split(',')[0]
    if (!raw) {
      this._hideDropdown(inputElement)
      return
    }
    const endpoint = this._endpointFor(raw, 1)
    try {
      const res = await fetch(endpoint, { signal: this.abortController.signal })
      if (!res.ok) return
      const json = await res.json()
      this._renderDropdown(inputElement, json)
    } catch (e) {
      if (e.name !== "AbortError") console.error(e)
    }
  }

  _endpointFor(q, page) {
    const params = new URLSearchParams({ page })
    if (this.typeValue === 'zip') {
      params.set('postal_code', q)
    } else {
      params.set('name', q)
    }
    const paths = {
      state: '/exhibitor/state_paginations',
      country: '/exhibitor/countries_paginations',
      city: '/exhibitor/cities',
      county: '/exhibitor/counties',
      zip: '/exhibitor/zips'
    }
    return paths[this.typeValue] + '?' + params.toString()
  }

  writeMirror(inputElement) {
    const mirror = document.getElementById(
      'location_' + inputElement.dataset.index + '_' + this.typeValue
    )
    if (mirror) mirror.value = inputElement.value
  }

  _renderDropdown(inputElement, json) {
    const dropdown = inputElement.parentElement.querySelector('[data-location-picker-target="dropdown"]')
    if (!dropdown) return

    // Clear previous content
    while (dropdown.firstChild) {
      dropdown.removeChild(dropdown.firstChild)
    }

    const { data, pagination } = json

    data.forEach((item) => {
      const label = this.formatLocationDisplay(item)
      const div = document.createElement('div')
      div.textContent = label
      div.style.cssText = 'padding:8px;cursor:pointer;font-size:10px'
      div.addEventListener('click', () => {
        inputElement.value = label
        this.writeMirror(inputElement)
        this._hideDropdown(inputElement)
        this.markDirty()
      })
      dropdown.appendChild(div)
    })

    // Pagination
    if (pagination && pagination.total_pages > 1) {
      this._appendPagination(dropdown, pagination, inputElement)
    }

    dropdown.style.display = 'block'
  }

  _appendPagination(container, pagination, inputElement) {
    const pagDiv = document.createElement('div')
    pagDiv.style.cssText = 'display:flex;flex-wrap:wrap;gap:2px;padding:4px;border-top:1px solid #eee'

    const q = inputElement.value.split(',')[0]

    const makeBtn = (label, page) => {
      const btn = document.createElement('button')
      btn.type = 'button'
      btn.className = 'pagination-btn'
      btn.style.fontSize = '8px'
      btn.textContent = label
      btn.addEventListener('click', () => {
        const endpoint = this._endpointFor(q, page)
        fetch(endpoint).then(res => res.json()).then(json => {
          this._renderDropdown(inputElement, json)
        })
      })
      return btn
    }

    const page = pagination.current_page
    const total = pagination.total_pages

    // First page button when not on page 1
    if (page > 1) {
      pagDiv.appendChild(makeBtn('1', 1))
      pagDiv.appendChild(makeBtn('<', page - 1))
    }

    // Windowed page numbers (at most 3)
    const pagesToShow = []
    if (total > 2) {
      if (page === 1 || page === 2) {
        pagesToShow.push(2, 3, total)
      } else if (page === total) {
        pagesToShow.push(total - 2, total - 1, total)
      } else {
        pagesToShow.push(page - 1, page, page + 1)
      }
    } else {
      for (let i = 2; i <= total; i++) pagesToShow.push(i)
    }

    // De-duplicate and clamp
    const uniquePages = [...new Set(pagesToShow)].filter(p => p >= 1 && p <= total)
    uniquePages.forEach(p => {
      pagDiv.appendChild(makeBtn(String(p), p))
    })

    // Next button when not on last page
    if (page < total) {
      pagDiv.appendChild(makeBtn('>', page + 1))
    }

    container.appendChild(pagDiv)
  }

  formatLocationDisplay(item) {
    switch (this.typeValue) {
      case 'state':
        return `${item.name}, ${item.country}`
      case 'country':
        return item.name
      case 'city':
        return `${item.name}, ${item.state}, ${item.country}`
      case 'county':
        return `${item.name}, ${item.state}, ${item.country}`
      case 'zip':
        return `${item.name}, ${item.state}, ${item.country}`
      default:
        return item.name
    }
  }

  _hideDropdown(inputElement) {
    const dropdown = inputElement.parentElement.querySelector('[data-location-picker-target="dropdown"]')
    if (dropdown) dropdown.style.display = 'none'
  }

  /* ---- form dirty tracking ---- */

  markDirty() {
    if (typeof window !== "undefined") window.editFormUpdated = true
    const saveButton = document.querySelector(".save-button-changes-class")
    if (saveButton) saveButton.setAttribute("data-bs-target", "#update-confirmation-modal")
  }

  /* ---- LRPO-5640 — write server-assigned ids back after autosave ---- */

  reconcileIds(detail) {
    if (!detail || !Array.isArray(detail.territory_locations)) return

    // Collect ids already present anywhere in the document — those rows are
    // saved and must not be re-claimed.
    const alreadyIdentified = new Set()
    const idRe = /territory\[territory_locations_attributes\]\[(\d+)\]\[id\]/
    document.querySelectorAll('input[name*="[id]"]').forEach(el => {
      const m = el.name.match(idRe)
      if (m) alreadyIdentified.add(el.closest('.form-field-wrapper')?.dataset.index ?? m[1])
    })

    // Seed with ids already bound in the DOM. Without this a newly added row can
    // claim an id a saved row already owns, and both submit the same [id] —
    // editing one rewrites the other, and deleting one destroys the other.
    const claimedIds = new Set(
      [...document.querySelectorAll('input[name$="[id]"]')]
        .map(el => String(el.value))
        .filter(v => v !== '')
    )

    // ---- pass 1: exact-value match ----

    for (const wrapper of this.element.querySelectorAll('.form-field-wrapper')) {
      // Skip rows that already have an [id] input (already saved).
      if (alreadyIdentified.has(wrapper.dataset.index)) continue
      if ([...wrapper.querySelectorAll('input')].some(inp => inp.name.match(idRe))) continue

      const i = wrapper.dataset.index
      const input = wrapper.querySelector('[data-location-picker-target="input"]')
      if (!input) continue
      const inputValue = input.value

      // Find the first matching server entry that has not been claimed yet.
      const entry = detail.territory_locations.find(e =>
        e.type === this.typeValue &&
        e.value === inputValue &&
        !claimedIds.has(String(e.id))
      )
      if (!entry) continue

      claimedIds.add(String(entry.id))
      this._injectIdInputs(i, entry.id)
    }

    // ---- pass 2: normalized-value fallback (1:1 only) ----

    // Collect rows in this.element that are STILL unclaimed after pass 1.
    const unclaimedRows = []
    for (const wrapper of this.element.querySelectorAll('.form-field-wrapper')) {
      if (alreadyIdentified.has(wrapper.dataset.index)) continue
      if ([...wrapper.querySelectorAll('input')].some(inp => inp.name.match(idRe))) continue
      const input = wrapper.querySelector('[data-location-picker-target="input"]')
      if (!input) continue
      unclaimedRows.push({ index: Number(wrapper.dataset.index), input })
    }

    // Collect server entries of our type that THIS request created and whose
    // id has not been claimed yet. Pre-existing duplicates (created: false /
    // absent) must never be bound — doing so corrupts unrelated records.
    const unclaimedEntries = detail.territory_locations.filter(e =>
      e.type === this.typeValue && e.created === true && !claimedIds.has(String(e.id))
    )

    // Only bind when there is exactly ONE of each — safe 1:1 mapping.
    if (unclaimedRows.length === 1 && unclaimedEntries.length === 1) {
      const row = unclaimedRows[0]
      const entry = unclaimedEntries[0]
      claimedIds.add(String(entry.id))
      this._injectIdInputs(row.index, entry.id)
      // Show what was actually stored so the user sees the canonical value.
      row.input.value = entry.value
    }
  }

  /* ---- helper: inject id + _destroy hidden inputs into hiddenFieldsTarget ---- */

  _injectIdInputs(index, recordId) {
    const idInput = document.createElement('input')
    idInput.type = 'hidden'
    idInput.setAttribute('name', `territory[territory_locations_attributes][${index}][id]`)
    idInput.setAttribute('id', `location_${index}_id`)
    idInput.setAttribute('value', String(recordId))

    const destroyInput = document.createElement('input')
    destroyInput.type = 'hidden'
    destroyInput.setAttribute('name', `territory[territory_locations_attributes][${index}][_destroy]`)
    destroyInput.setAttribute('id', `location_${index}_destroy`)
    destroyInput.setAttribute('value', 'false')

    // These go in the hiddenFields container, NOT in the wrapper — exactly
    // where a server-rendered saved row keeps its mirror. deleteRow removes
    // the wrapper, so a _destroy living inside it would be removed too and
    // the row would silently survive in the database instead of being
    // destroyed. There is a spec for that.
    this.hiddenFieldsTarget.appendChild(idInput)
    this.hiddenFieldsTarget.appendChild(destroyInput)
  }
}
